import {
  AddToCalendarIcon,
  CloseIcon,
  DeleteIcon,
  DocumentList,
  DocumentListItem,
  EditIcon,
  HTMLDisplay,
  IconButton,
  ResourceMenuActions,
  Stack,
  Tag,
  Typography,
  WarningIcon,
} from '@all-manager/design-system-v2';
import { EventById } from '../features/events/api/useGetEventById';
import CreateUpdateEventModal from '../features/events/external-components/CreateUpdateEventModal/CreateUpdateEventModal';
import DeleteEventModal from '../features/events/external-components/DeleteEventModal/DeleteEventModal';
import TopicLink from '../features/topics/components/TopicLink/TopicLink';
import { formatDates } from '../utils/formatValues';
import { useEventContent } from './useEventContent';

type Props = {
  event: EventById;
  hideDate?: boolean;
  /** Hide the title when the surrounding surface already shows it (e.g. a dialog title). */
  hideTitle?: boolean;
  onClose?: () => void;
  /** Called once the event is deleted, e.g. to close the surface showing it. */
  onDeleted?: () => void;
};

export default function EventContent(props: Props) {
  const hook = useEventContent(props);
  const documents = props.event.documents ?? [];

  return (
    <>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
          <Stack spacing={0.5}>
            {props.event.isImportant ? (
              <Stack direction="row">
                <Tag label="Important" color="incident" icon={<WarningIcon />} />
              </Stack>
            ) : null}

            {props.event.subject_id ? (
              <TopicLink subTopicId={props.event.subject_id} variant="caption" />
            ) : null}

            {props.hideTitle ? null : (
              <Typography variant="h3">
                {props.event.title}
              </Typography>
            )}

            {props.hideDate ? null : (
              <Typography variant="body2Emphasis">
                {formatDates(props.event.date, props.event.end_date, props.event.all_day)}
              </Typography>
            )}
          </Stack>

          <Stack direction="row" spacing={0.5} alignItems="center">
            <ResourceMenuActions
              size="small"
              tooltip="Options de l'événement"
              actions={[
                {
                  id: 'google-calendar',
                  label: 'Ajouter à mon agenda Google',
                  icon: <AddToCalendarIcon fontSize="small" />,
                  onClick: hook.handleAddToGoogleCalendar,
                },
                hook.isAdmin ? { type: 'divider', id: 'admin-divider' } : null,
                hook.isAdmin ? {
                  id: 'edit',
                  label: 'Modifier',
                  icon: <EditIcon fontSize="small" />,
                  onClick: hook.handleOpenEditModal,
                } : null,
                hook.isAdmin ? {
                  id: 'delete',
                  label: 'Supprimer',
                  icon: <DeleteIcon fontSize="small" />,
                  onClick: hook.handleOpenDeleteModal,
                } : null,
              ]}
            />
            {props.onClose ? (
              <IconButton size="small" tooltip="Fermer" onClick={props.onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Stack>
        </Stack>

        {props.event.provider_name || props.event.contact_name ? (
          <Stack spacing={0.5}>
            {props.event.provider_name ? (
              <Stack direction="row" spacing={0.5} wrap="wrap">
                <Typography variant="body2">Prestataire :</Typography>
                <Typography variant="body2Emphasis">{props.event.provider_name}</Typography>
              </Stack>
            ) : null}
            {props.event.contact_name ? (
              <Stack direction="row" spacing={0.5} wrap="wrap">
                <Typography variant="body2">Personne(s) présente(s) :</Typography>
                <Typography variant="body2Emphasis">{props.event.contact_name}</Typography>
              </Stack>
            ) : null}
          </Stack>
        ) : null}

        {props.event.description ? (
          <HTMLDisplay content={props.event.description} />
        ) : null}

        {documents.length > 0 ? (
          <DocumentList aria-label="Documents de l'événement">
            {documents.map((doc) => (
              <DocumentListItem
                key={doc.id}
                name={doc.name ?? 'Document sans nom'}
                href={doc.file_url}
                type={doc.document_types?.name ?? doc.type}
              />
            ))}
          </DocumentList>
        ) : null}
      </Stack>

      {hook.isEditModalOpen ? (
        <CreateUpdateEventModal
          onClose={hook.handleCloseEditModal}
          subTopicId={props.event.subject_id ?? ''}
          eventId={props.event.id}
        />
      ) : null}

      {hook.isDeleteModalOpen ? (
        <DeleteEventModal
          event={props.event}
          onClose={hook.handleCloseDeleteModal}
          onDeleted={props.onDeleted}
        />
      ) : null}
    </>
  );
}
